import type { Tag } from '@/data/logs';
import { $tags } from '@/stores';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { colord } from 'colord';
import { useEffect, useRef, useState } from 'react';

function tagBadgeColor(tagColor: string) {
  const color = colord(tagColor).toHsl();
  if (color.s < 1 || color.l > 99) {
    return '#000000';
  } else {
    return colord({
      h: color.h,
      s: 100,
      l: 30,
    }).toHex();
  }
}

export function TagBadge({
  tag,
  removeTag,
}: {
  tag: Tag;
  removeTag?: () => void;
}) {
  return (
    <div
      className="flex flex-row rounded border py-1 text-sm font-medium"
      style={{
        backgroundColor: tag.color + '22',
        borderColor: tag.color,
        color: tagBadgeColor(tag.color),
      }}
    >
      <span className={clsx(removeTag ? 'ml-2' : 'mx-2')}>{tag.name}</span>
      {removeTag && (
        <button
          className="cursor-pointer px-2 text-xs font-bold"
          onClick={() => removeTag()}
        >
          &times;
        </button>
      )}
    </div>
  );
}

export function TagsComponent({
  tagIds,
  addTag,
  removeTag,
  left = false,
}: {
  tagIds: string[];
  addTag?: (tag: Tag) => void;
  removeTag?: (tagId: string) => void;
  left?: boolean;
}) {
  const tags = useStore($tags);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedTags = tags.filter(tag => tagIds.includes(tag.id));
  const availableTags = tags.filter(tag => !tagIds.includes(tag.id));

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  const handleAddTag = (tag: Tag) => {
    if (!addTag) return;
    addTag(tag);
    setIsMenuOpen(false);
  };

  return (
    <>
      {selectedTags.map(tag => (
        <TagBadge
          key={tag.id}
          tag={tag}
          removeTag={removeTag ? () => removeTag(tag.id) : undefined}
        />
      ))}
      {addTag && selectedTags.length === 0 && (
        <div className="relative inline-block" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="inline-block cursor-pointer rounded border border-gray-400 px-2 py-1 text-sm font-medium hover:bg-gray-100"
            title="Add tag"
          >
            +
          </button>
          {isMenuOpen && (
            <div
              className={clsx(
                'absolute z-10 mt-1 max-h-60 min-w-37.5 overflow-y-auto rounded border border-gray-300 bg-white shadow-lg',
                left ? 'left-0' : 'right-0',
              )}
            >
              {availableTags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => handleAddTag(tag)}
                  className="flex w-full cursor-pointer items-center px-3 py-2 text-left hover:bg-gray-100"
                >
                  <span
                    className="mr-2 inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="text-sm">{tag.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
